import { useState, useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  DollarSign,
  BarChart3,
  Wallet,
  Building2,
  Activity,
  Scale,
} from 'lucide-react';
import Card, { CardHeader, CardBody } from '../ui/Card';

// Existing SEC visualization components
import FinancialFlowSankey from '../visualizations/FinancialFlowSankey';
import IncomeStatementTrends from '../visualizations/IncomeStatementTrends';
import BalanceSheetTrends from '../visualizations/BalanceSheetTrends';
import CashFlowWaterfall from '../visualizations/CashFlowWaterfall';
import CapitalAllocationDashboard from '../visualizations/CapitalAllocationDashboard';
import OperatingExpenseBreakdown from '../visualizations/OperatingExpenseBreakdown';
import ComparativeBalanceSheetTable from '../visualizations/ComparativeBalanceSheetTable';
import ComparativeIncomeStatementTable from '../visualizations/ComparativeIncomeStatementTable';
import ComparativeCashFlowTable from '../visualizations/ComparativeCashFlowTable';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface Report {
  reportId: string;
  type: string;
  period: string;
  filingDate: string;
  metrics: {
    revenue?: string;
    netIncome?: string;
    grossProfit?: string;
    operatingIncome?: string;
    totalAssets?: string;
    totalLiabilities?: string;
    shareholdersEquity?: string;
  };
  valuation?: any;
  balanceSheet?: any;
  incomeStatement?: any;
  cashFlow?: any;
  ratios?: any;
}

interface SECFilingsTabProps {
  reports: Report[];
  companyName: string;
  companyCountry: string;
}

// ─── Utility Functions ──────────────────────────────────────────────────────────

/**
 * Parse a formatted currency string into billions.
 * Handles: "$113.7B", "113,743 million", "$42.1B", plain numbers (treated as raw → /1e9).
 * Returns value in BILLIONS for consistent internal use.
 */
const parseToBillions = (value?: string | number | null): number | null => {
  if (value == null) return null;
  if (typeof value === 'number') return value / 1_000_000_000;
  const clean = value.replace(/[$,]/g, '').trim();
  if (/[Tt](?:rillion)?/i.test(clean)) {
    return parseFloat(clean.replace(/[A-Za-z\s]/g, '')) * 1_000;
  }
  if (/[Bb](?:illion)?/i.test(clean)) {
    return parseFloat(clean.replace(/[A-Za-z\s]/g, ''));
  }
  if (/[Mm](?:illion)?/i.test(clean)) {
    return parseFloat(clean.replace(/[A-Za-z\s]/g, '')) / 1_000;
  }
  const num = parseFloat(clean);
  if (isNaN(num)) return null;
  // Large raw numbers (> 10,000) are likely in millions or raw dollars
  if (Math.abs(num) >= 1_000_000) return num / 1_000_000_000;
  if (Math.abs(num) >= 1_000) return num / 1_000; // Millions → billions
  return num; // Already in billions range
};

const formatBillions = (value: number | null): string => {
  if (value == null) return 'N/A';
  const abs = Math.abs(value);
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(1)}T`;
  if (abs >= 1) return `$${value.toFixed(1)}B`;
  if (abs >= 0.001) return `$${(value * 1_000).toFixed(0)}M`;
  return `$${(value * 1_000_000).toFixed(0)}K`;
};


const formatPercent = (value: number | null | undefined): string => {
  if (value == null) return 'N/A';
  return `${(value * 100).toFixed(1)}%`;
};

const formatRatio = (value: number | null | undefined): string => {
  if (value == null) return 'N/A';
  return value.toFixed(2);
};

// ─── Section Divider (matching Indian enterprise style) ─────────────────────────

function SectionDivider({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-3 pt-6 pb-2">
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-neutral-200 to-neutral-200" />
      <span className="text-xs font-bold text-neutral-400 uppercase tracking-widest px-1 shrink-0">
        {title}
      </span>
      <div className="h-px flex-1 bg-gradient-to-l from-transparent via-neutral-200 to-neutral-200" />
    </div>
  );
}

// ─── KPI Summary Card ───────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string;
  sublabel?: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
}

function KpiCard({ label, value, sublabel, icon: Icon, iconBg, iconColor, trend, trendValue }: KpiCardProps) {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-2">
        <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">{label}</span>
        <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
      </div>
      <div className="text-xl font-bold text-neutral-900">{value}</div>
      <div className="flex items-center gap-1.5 mt-1">
        {sublabel && <span className="text-xs text-neutral-400">{sublabel}</span>}
        {trend && trendValue && (
          <span className={`text-xs font-medium flex items-center gap-0.5 ${
            trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-neutral-400'
          }`}>
            {trend === 'up' ? <TrendingUp className="w-3 h-3" /> :
             trend === 'down' ? <TrendingDown className="w-3 h-3" /> :
             <Minus className="w-3 h-3" />}
            {trendValue}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Financial Statements Sub-Tab Navigation ────────────────────────────────────

type StatementTab = 'income' | 'balance' | 'cashflow';

interface StatementTabNavProps {
  activeStatement: StatementTab;
  onTabChange: (tab: StatementTab) => void;
}

function StatementTabNav({ activeStatement, onTabChange }: StatementTabNavProps) {
  const tabs: { id: StatementTab; label: string }[] = [
    { id: 'income', label: 'Income Statement' },
    { id: 'balance', label: 'Balance Sheet' },
    { id: 'cashflow', label: 'Cash Flow Statement' },
  ];

  return (
    <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-1">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`px-5 py-2 text-sm font-medium rounded-md transition-colors ${
            activeStatement === tab.id
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-neutral-600 hover:text-neutral-900'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ─── Period Controls ────────────────────────────────────────────────────────────

interface PeriodControlsProps {
  viewMode: 'single' | 'annual' | 'quarterly';
  maxPeriods: number;
  onViewModeChange: (mode: 'single' | 'annual' | 'quarterly') => void;
  onMaxPeriodsChange: (count: number) => void;
}

function PeriodControls({ viewMode, maxPeriods, onViewModeChange, onMaxPeriodsChange }: PeriodControlsProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">View:</span>
        <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-0.5">
          {(['single', 'annual', 'quarterly'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => onViewModeChange(mode)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === mode
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-neutral-500 hover:text-neutral-800'
              }`}
            >
              {mode === 'single' ? 'Latest' : mode === 'annual' ? 'Annual' : 'Quarterly'}
            </button>
          ))}
        </div>
      </div>

      {viewMode !== 'single' && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Periods:</span>
          <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-0.5">
            {[3, 5, 10].map((count) => (
              <button
                key={count}
                onClick={() => onMaxPeriodsChange(count)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  maxPeriods === count
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-800'
                }`}
              >
                {count}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Liquidity Health Inline Card ───────────────────────────────────────────────

function LiquidityHealthInline({ balanceSheet }: { balanceSheet: any }) {
  const currentRatio = balanceSheet?.currentRatio;
  const quickRatio = balanceSheet?.quickRatio;
  const workingCapital = parseToBillions(balanceSheet?.workingCapital);

  const getStatus = (ratio: number | null | undefined, good: number, warn: number) => {
    if (ratio == null) return { label: 'N/A', color: 'neutral', bg: 'bg-neutral-50', border: 'border-neutral-200' };
    if (ratio >= good) return { label: 'Healthy', color: 'green', bg: 'bg-green-50', border: 'border-green-200' };
    if (ratio >= warn) return { label: 'Monitor', color: 'yellow', bg: 'bg-yellow-50', border: 'border-yellow-200' };
    return { label: 'At Risk', color: 'red', bg: 'bg-red-50', border: 'border-red-200' };
  };

  const crStatus = getStatus(currentRatio, 1.5, 1.0);
  const qrStatus = getStatus(quickRatio, 1.0, 0.7);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div className={`rounded-lg p-3 ${crStatus.bg} border ${crStatus.border}`}>
        <div className="text-xs font-medium text-neutral-500 mb-1">Current Ratio</div>
        <div className="text-lg font-bold text-neutral-900">{formatRatio(currentRatio)}</div>
        <div className={`text-[10px] font-bold uppercase tracking-wider text-${crStatus.color}-600 mt-0.5`}>
          {crStatus.label}
        </div>
      </div>
      <div className={`rounded-lg p-3 ${qrStatus.bg} border ${qrStatus.border}`}>
        <div className="text-xs font-medium text-neutral-500 mb-1">Quick Ratio</div>
        <div className="text-lg font-bold text-neutral-900">{formatRatio(quickRatio)}</div>
        <div className={`text-[10px] font-bold uppercase tracking-wider text-${qrStatus.color}-600 mt-0.5`}>
          {qrStatus.label}
        </div>
      </div>
      <div className={`rounded-lg p-3 ${workingCapital != null && workingCapital > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}>
        <div className="text-xs font-medium text-neutral-500 mb-1">Working Capital</div>
        <div className="text-lg font-bold text-neutral-900">
          {workingCapital != null ? formatBillions(workingCapital) : 'N/A'}
        </div>
        <div className={`text-[10px] font-bold uppercase tracking-wider ${
          workingCapital != null && workingCapital > 0 ? 'text-green-600' : 'text-red-600'
        } mt-0.5`}>
          {workingCapital != null ? (workingCapital > 0 ? 'Positive' : 'Negative') : 'N/A'}
        </div>
      </div>
    </div>
  );
}

// ─── Main SEC Filings Tab Component ─────────────────────────────────────────────

export default function SECFilingsTab({ reports, companyName, companyCountry }: SECFilingsTabProps) {
  // Statement sub-tab state
  const [activeStatement, setActiveStatement] = useState<StatementTab>('income');

  // Unified period controls
  const [viewMode, setViewMode] = useState<'single' | 'annual' | 'quarterly'>('annual');
  const [maxPeriods, setMaxPeriods] = useState(5);

  const latestReport = reports[0];
  const previousReport = reports.length > 1 ? reports[1] : null;

  // ─── Compute KPI metrics (all values in billions) ──────────────────────
  const kpis = useMemo(() => {
    if (!latestReport?.metrics) return null;

    const revenue = parseToBillions(latestReport.metrics.revenue);
    const netIncome = parseToBillions(latestReport.metrics.netIncome);
    const grossProfit = parseToBillions(latestReport.metrics.grossProfit);
    const operatingIncome = parseToBillions(latestReport.metrics.operatingIncome);
    const totalAssets = parseToBillions(latestReport.metrics.totalAssets ?? latestReport.balanceSheet?.totalAssets);
    const totalLiabilities = parseToBillions(latestReport.metrics.totalLiabilities ?? latestReport.balanceSheet?.totalLiabilities);
    const shareholdersEquity = parseToBillions(latestReport.metrics.shareholdersEquity ?? latestReport.balanceSheet?.shareholdersEquity);

    // Calculate trends if previous report exists
    const prevRevenue = previousReport ? parseToBillions(previousReport.metrics.revenue) : null;
    const revGrowth = (revenue != null && prevRevenue != null && prevRevenue !== 0)
      ? ((revenue - prevRevenue) / Math.abs(prevRevenue))
      : null;

    const grossMargin = (grossProfit != null && revenue != null && revenue !== 0) ? grossProfit / revenue : null;
    const operatingMargin = (operatingIncome != null && revenue != null && revenue !== 0) ? operatingIncome / revenue : null;
    const netMargin = (netIncome != null && revenue != null && revenue !== 0) ? netIncome / revenue : null;

    return {
      revenue, netIncome, grossProfit, operatingIncome, totalAssets, totalLiabilities, shareholdersEquity,
      revGrowth, grossMargin, operatingMargin, netMargin,
    };
  }, [latestReport, previousReport]);

  // ─── Parse OpEx data for operating expense breakdown ──────────────────
  // Child component (OperatingExpenseBreakdown) expects values in raw dollars
  const opExData = useMemo(() => {
    if (!latestReport?.incomeStatement) return null;
    const inc = latestReport.incomeStatement;
    const rdB = parseToBillions(inc.researchDevelopment);
    const smB = parseToBillions(inc.salesMarketing);
    const gaB = parseToBillions(inc.generalAdministrative);
    if (rdB == null && smB == null && gaB == null) return null;
    const revenueB = parseToBillions(inc.revenue);
    const totalOpExB = parseToBillions(inc.totalOperatingExpenses) ?? ((rdB ?? 0) + (smB ?? 0) + (gaB ?? 0));
    // Convert billions → raw dollars (* 1e9) for child component
    return {
      researchDevelopment: (rdB ?? 0) * 1e9,
      salesMarketing: (smB ?? 0) * 1e9,
      generalAdministrative: (gaB ?? 0) * 1e9,
      totalOperatingExpenses: totalOpExB * 1e9,
      revenue: (revenueB ?? 0) * 1e9,
      period: inc.period || latestReport.period,
    };
  }, [latestReport]);

  if (!reports.length) {
    return (
      <Card>
        <CardBody className="text-center py-12">
          <Activity className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
          <p className="text-sm text-neutral-500">No SEC filings data available.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* ═══════════════ SECTION 1: Key Financial Highlights (KPI Cards) ═══════════════ */}
      {/* NOTE: P&L / Balance Sheet / Sankey are in the separate "Finance" tab via FinancialDashboardTab */}
      <SectionDivider title="Key Financial Highlights" />

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard
            label="Revenue"
            value={formatBillions(kpis.revenue)}
            sublabel={latestReport.period}
            icon={DollarSign}
            iconBg="bg-blue-50"
            iconColor="text-blue-600"
            trend={kpis.revGrowth != null ? (kpis.revGrowth > 0.005 ? 'up' : kpis.revGrowth < -0.005 ? 'down' : 'neutral') : undefined}
            trendValue={kpis.revGrowth != null ? `${(kpis.revGrowth * 100).toFixed(1)}% YoY` : undefined}
          />
          <KpiCard
            label="Gross Profit"
            value={formatBillions(kpis.grossProfit)}
            sublabel={kpis.grossMargin != null ? `Margin: ${(kpis.grossMargin * 100).toFixed(1)}%` : undefined}
            icon={BarChart3}
            iconBg="bg-indigo-50"
            iconColor="text-indigo-600"
          />
          <KpiCard
            label="Operating Income"
            value={formatBillions(kpis.operatingIncome)}
            sublabel={kpis.operatingMargin != null ? `Margin: ${(kpis.operatingMargin * 100).toFixed(1)}%` : undefined}
            icon={TrendingUp}
            iconBg="bg-teal-50"
            iconColor="text-teal-600"
          />
          <KpiCard
            label="Net Income"
            value={formatBillions(kpis.netIncome)}
            sublabel={kpis.netMargin != null ? `Margin: ${(kpis.netMargin * 100).toFixed(1)}%` : undefined}
            icon={Wallet}
            iconBg="bg-green-50"
            iconColor="text-green-600"
          />
          <KpiCard
            label="Total Assets"
            value={formatBillions(kpis.totalAssets)}
            icon={Building2}
            iconBg="bg-violet-50"
            iconColor="text-violet-600"
          />
          <KpiCard
            label="Equity"
            value={formatBillions(kpis.shareholdersEquity)}
            icon={Scale}
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
          />
        </div>
      )}

      {/* ═══════════════ SECTION 3: Liquidity & Health ═══════════════ */}
      {latestReport.balanceSheet && (latestReport.balanceSheet.currentRatio != null || latestReport.balanceSheet.workingCapital != null) && (
        <>
          <SectionDivider title="Liquidity Health" />
          <LiquidityHealthInline balanceSheet={latestReport.balanceSheet} />
        </>
      )}

      {/* ═══════════════ SECTION 4: Revenue Flow Visualization ═══════════════ */}
      {latestReport.metrics && (
        <>
          <SectionDivider title="Revenue Flow" />
          <FinancialFlowSankey
            data={{
              revenue: latestReport.metrics.revenue,
              grossProfit: latestReport.metrics.grossProfit,
              operatingIncome: latestReport.metrics.operatingIncome,
              netIncome: latestReport.metrics.netIncome,
            }}
            period={latestReport.period}
          />
        </>
      )}

      {/* ═══════════════ SECTION 5: Financial Statement Trends ═══════════════ */}
      <SectionDivider title="Historical Trends" />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <IncomeStatementTrends reports={reports} compact />
        <BalanceSheetTrends reports={reports} compact />
      </div>

      {/* ═══════════════ SECTION 6: Comparative Financial Statements ═══════════════ */}
      <SectionDivider title="Comparative Financial Statements" />

      <Card>
        <CardBody className="space-y-4">
          {/* Controls Row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <StatementTabNav activeStatement={activeStatement} onTabChange={setActiveStatement} />
            <PeriodControls
              viewMode={viewMode}
              maxPeriods={maxPeriods}
              onViewModeChange={setViewMode}
              onMaxPeriodsChange={setMaxPeriods}
            />
          </div>
        </CardBody>
      </Card>

      {/* Active Statement Table */}
      {activeStatement === 'income' && latestReport.incomeStatement && (
        <ComparativeIncomeStatementTable reports={reports} mode={viewMode} maxPeriods={maxPeriods} />
      )}
      {activeStatement === 'balance' && latestReport.balanceSheet && (
        <ComparativeBalanceSheetTable reports={reports} mode={viewMode} maxPeriods={maxPeriods} />
      )}
      {activeStatement === 'cashflow' && latestReport.cashFlow && (
        <ComparativeCashFlowTable reports={reports} mode={viewMode} maxPeriods={maxPeriods} />
      )}

      {/* ═══════════════ SECTION 7: Cash Flow Waterfall (full width, own row) ═══════════════ */}
      {latestReport.cashFlow?.netCashFromOperations && (parseToBillions(latestReport.cashFlow.netCashFromOperations) ?? 0) !== 0 && (
        <>
          <SectionDivider title="Cash Flow Waterfall" />
          <CashFlowWaterfall
            data={{
              operatingCashFlow: (parseToBillions(latestReport.cashFlow.netCashFromOperations) ?? 0) * 1e9,
              investingCashFlow: (parseToBillions(latestReport.cashFlow.netCashFromInvesting) ?? 0) * 1e9,
              financingCashFlow: (parseToBillions(latestReport.cashFlow.netCashFromFinancing) ?? 0) * 1e9,
              period: latestReport.period,
            }}
          />
        </>
      )}

      {/* ═══════════════ SECTION 8: Capital Allocation + Operating Expenses (side-by-side) ═══════════════ */}
      {(latestReport.cashFlow?.dividendsPaid || opExData) && (
        <>
          <SectionDivider title="Capital Allocation & Operating Expenses" />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {latestReport.cashFlow?.dividendsPaid && (
              <CapitalAllocationDashboard
                data={{
                  dividendsPaid: Math.abs(parseToBillions(latestReport.cashFlow.dividendsPaid) ?? 0) * 1e9,
                  shareRepurchases: Math.abs(parseToBillions(latestReport.cashFlow.stockRepurchases) ?? 0) * 1e9,
                  operatingCashFlow: (parseToBillions(latestReport.cashFlow.netCashFromOperations) ?? 0) * 1e9,
                  period: latestReport.period,
                }}
              />
            )}
            {opExData && (
              <OperatingExpenseBreakdown data={opExData} />
            )}
          </div>
        </>
      )}

      {/* NOTE: Financial Ratios are in "Finance" tab; Valuation is in "EV Insights" tab */}
    </div>
  );
}
